// ============================================================
// openBill.ts — open ONE bill directly: its case, on the Billing section,
// with that bill's view open. Shared by the bill notifications (the bell)
// and the Activity feed's invoice / payment / credit-note entries.
//
// The bill is loaded FIRST (it names its case), and only then does the app
// navigate — so a slow or failed load leaves the user where they were rather
// than stranded on the case list. Navigation mirrors the sidebar's
// takeover-then-navigate pattern: flush autosaves, close takeovers, open the
// case with the section + bill queued (ViewingPane consumes
// caseInitialSection; CaseBillingSection consumes pendingBill, seeded with the
// loaded bill so it paints at once).
// Read-only: it only loads + displays — nothing about the bill changes.
// ============================================================
import { getBillApi } from '../api/casevoApi'
import { navigateTo } from '../navigation/navigate'
import { flushAllAutoSaves } from '../hooks/flushAllAutoSaves'
import type { Bill } from '../types/billing.types'

// Resolves true once navigation has started, false if the bill couldn't be loaded.
export const openBillById = async (billId: string): Promise<boolean> => {
  let bill: Bill
  try { bill = await getBillApi(billId) } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[openBillById] Failed to open bill:', err)
    return false
  }
  // ★ (Audit) Persist any pending edit BEFORE closing the open record.
  await flushAllAutoSaves()
  void navigateTo({ view: 'caseDetail', caseId: bill.case.id }, {
    skipFlush: true,
    intents: (s) => {
      s.setPendingBill({ billId: bill.id, caseId: bill.case.id, initial: bill })
      s.setCaseInitialSection('billing')
      s.setCaseOpenedFrom('caseList')
    },
  }).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[openBillById] Failed to open the bill’s case:', err)
  })
  return true
}
